import { Database, LockKeyhole, MapPinned, RefreshCw, ShieldCheck } from "lucide-react";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { AtlasMap } from "./components/AtlasMap";
import { DirectoryTable } from "./components/DirectoryTable";
import { MobileInquiry } from "./components/MobileInquiry";
import { UpdateLog } from "./components/UpdateLog";
import { scopeLabels } from "./data/provinces";
import type { DatasetMeta, Scope, University, UpdateLogEntry } from "./types";
import {
  filterUniversities,
  getProvinceStats,
  getSelectedProvinceName,
  getUniversitiesByProvince
} from "./utils/data";

const scopes: Scope[] = ["all", "ordinary", "undergraduate", "vocational", "private", "military"];
const StatsPanel = lazy(() =>
  import("./components/StatsPanel").then((module) => ({ default: module.StatsPanel }))
);

function isMobileEntryRoute() {
  const params = new URLSearchParams(window.location.search);
  return window.location.pathname === "/m" || params.get("entry") === "wechat";
}

function App() {
  const [universities, setUniversities] = useState<University[]>([]);
  const [meta, setMeta] = useState<DatasetMeta | null>(null);
  const [log, setLog] = useState<UpdateLogEntry[]>([]);
  const [activeScope, setActiveScope] = useState<Scope>("all");
  const [selectedProvince, setSelectedProvince] = useState<string | null>(null);
  const [selectedUniversity, setSelectedUniversity] = useState<University | null>(null);
  const [query, setQuery] = useState("");
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">("loading");
  const isMobileEntry = isMobileEntryRoute();

  useEffect(() => {
    async function loadData() {
      try {
        const [universitiesResponse, metaResponse, logResponse] = await Promise.all([
          fetch("/data/universities.json"),
          fetch("/data/metadata.json"),
          fetch("/data/update-log.json")
        ]);

        if (!universitiesResponse.ok || !metaResponse.ok || !logResponse.ok) {
          throw new Error("Failed to load prototype data");
        }

        setUniversities(await universitiesResponse.json());
        setMeta(await metaResponse.json());
        setLog(await logResponse.json());
        setLoadState("ready");
      } catch (error) {
        console.error(error);
        setLoadState("error");
      }
    }

    loadData();
  }, []);

  const scopedUniversities = useMemo(
    () => filterUniversities(universities, activeScope),
    [activeScope, universities]
  );

  const provinceStats = useMemo(
    () => getProvinceStats(universities, activeScope),
    [activeScope, universities]
  );

  const directoryRows = useMemo(
    () => getUniversitiesByProvince(universities, selectedProvince, activeScope),
    [activeScope, selectedProvince, universities]
  );

  useEffect(() => {
    setSelectedUniversity(null);
  }, [activeScope, selectedProvince]);

  const selectedProvinceName = getSelectedProvinceName(selectedProvince);

  if (loadState === "error") {
    return (
      <main className="load-failure">
        <h1>数据加载失败</h1>
        <p>请确认 `public/data` 下的高校数据、元信息和更新记录存在。</p>
      </main>
    );
  }

  if (isMobileEntry) {
    return (
      <MobileInquiry
        universities={universities}
        meta={meta}
        provinceStats={provinceStats}
        isLoading={loadState === "loading"}
      />
    );
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <a className="brand" href="#top" aria-label="教育信息可视化首页">
          <span className="brand-mark">EDU</span>
          <span>教育信息可视化</span>
        </a>
        <nav className="topnav" aria-label="主导航">
          <a href="#map">地图</a>
          <a href="#directory">目录</a>
          <a href="#stats">统计</a>
          <a href="#log">Log</a>
        </nav>
      </header>

      <main id="top">
        <section className="hero-band">
          <div className="hero-copy">
            <p className="eyebrow">PRIVATE EDU ATLAS</p>
            <h1>高校数据地图工作台</h1>
            <p>
              先把 China Edu-Atlas 的核心体验跑起来：省份分布、目录检索、统计图表和维护日志。
              后续把数据来源换成教育部 Excel 管线，就能变成可审计的私有化面板。
            </p>
          </div>

          <div className="metric-strip" aria-label="数据概览">
            <div>
              <span>记录</span>
              <strong>{meta?.record_count ?? scopedUniversities.length}</strong>
            </div>
            <div>
              <span>本科</span>
              <strong>{meta?.undergraduate_count ?? 0}</strong>
            </div>
            <div>
              <span>专科</span>
              <strong>{meta?.vocational_count ?? 0}</strong>
            </div>
            <div>
              <span>军校</span>
              <strong>{meta?.military_count ?? 0}</strong>
            </div>
          </div>
        </section>

        <section className="control-band" aria-label="数据范围筛选">
          <div className="scope-tabs" role="tablist" aria-label="高校范围">
            {scopes.map((scope) => (
              <button
                key={scope}
                type="button"
                className={activeScope === scope ? "is-active" : ""}
                onClick={() => {
                  setActiveScope(scope);
                  setSelectedProvince(null);
                }}
              >
                {scopeLabels[scope]}
              </button>
            ))}
          </div>
          <div className="source-note">
            <ShieldCheck size={16} />
            <span>{meta?.note ?? "加载数据元信息中"}</span>
          </div>
        </section>

        <div id="map" className="anchor-offset">
          <AtlasMap
            stats={provinceStats}
            selectedProvince={selectedProvince}
            activeScope={activeScope}
            onSelectProvince={setSelectedProvince}
          />
        </div>

        <section className="detail-band" aria-label="所选学校详情">
          <div className="detail-summary">
            <MapPinned size={18} />
            <div>
              <span>当前范围</span>
              <strong>
                {selectedProvinceName}，{scopeLabels[activeScope]}，{directoryRows.length} 条
              </strong>
            </div>
          </div>
          <div className="detail-summary">
            <Database size={18} />
            <div>
              <span>目录状态</span>
              <strong>{query ? `搜索 “${query}”` : "未搜索"}</strong>
            </div>
          </div>
          <div className="detail-summary">
            <LockKeyhole size={18} />
            <div>
              <span>部署方向</span>
              <strong>Cloudflare Pages + Access</strong>
            </div>
          </div>
        </section>

        <div id="directory" className="anchor-offset">
          <DirectoryTable
            rows={directoryRows}
            query={query}
            selectedUniversity={selectedUniversity}
            onQueryChange={setQuery}
            onSelectUniversity={setSelectedUniversity}
          />
        </div>

        {selectedUniversity && (
          <section className="school-inspector" aria-label="学校详情">
            <div>
              <p className="eyebrow">SELECTED SCHOOL</p>
              <h2>{selectedUniversity.name}</h2>
            </div>
            <dl>
              <div>
                <dt>标识码</dt>
                <dd>{selectedUniversity.code || "-"}</dd>
              </div>
              <div>
                <dt>主管部门</dt>
                <dd>{selectedUniversity.department || "-"}</dd>
              </div>
              <div>
                <dt>所在地</dt>
                <dd>{selectedUniversity.location || "-"}</dd>
              </div>
              <div>
                <dt>办学层次</dt>
                <dd>{selectedUniversity.level || "未标注"}</dd>
              </div>
              <div>
                <dt>备注</dt>
                <dd>{selectedUniversity.remark || "普通"}</dd>
              </div>
            </dl>
          </section>
        )}

        <div id="stats" className="anchor-offset">
          <Suspense
            fallback={
              <section className="stats-shell" aria-label="统计图表加载中">
                <div className="chart-loading">正在加载统计图表</div>
              </section>
            }
          >
            <StatsPanel universities={universities} stats={provinceStats} activeScope={activeScope} />
          </Suspense>
        </div>

        <div id="log" className="anchor-offset">
          <UpdateLog meta={meta} log={log} />
        </div>

        <section className="deploy-note">
          <RefreshCw size={18} />
          <p>
            下一步建议补 `scripts/build-universities.ts`：下载教育部 Excel，合并军校补充表，生成
            `universities.json`、`metadata.json` 和 `update-log.json`，再由 Cloudflare Pages 发布静态产物。
          </p>
        </section>
      </main>
    </div>
  );
}

export default App;
